import qs from 'qs';
import apiClient from '@/lib/apiClient';

export interface DownloadCsvFromPostArgs {
  /** Path under the gateway, e.g. `api/supplier-experience/playable-invoices/csv` */
  path: string;
  body: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  filename: string;
}

/**
 * POST a CSV export endpoint and trigger a browser download from the response blob.
 */
export const downloadCsvFromPost = async ({ path, body, query, filename }: DownloadCsvFromPostArgs): Promise<void> => {
  const params = query ? qs.stringify(query, { addQueryPrefix: true, skipNulls: true }) : '';
  const response = await apiClient.post(`${path}${params}`, { json: body });

  if (!response.ok) {
    throw new Error(`CSV download failed with status ${response.status}`);
  }

  const blob = await response.blob();
  triggerBlobDownload(blob, filename);
};

export const parseContentDispositionFilename = (header: string | null): string | undefined => {
  if (!header) {
    return undefined;
  }

  // filename*=UTF-8''encoded-name.csv
  const utf8Match = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^["']|["']$/g, ''));
    } catch {
      // fall through to filename=
    }
  }

  const basicMatch = /filename=(?:"([^"]+)"|([^;]+))/i.exec(header);
  const raw = basicMatch?.[1] ?? basicMatch?.[2];
  return raw?.trim() || undefined;
};

export const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};
